import React, { useState } from 'react';
import { Alert, Button, Card, Container, Form, Spinner } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { ROUTES } from '../config/appConfig';
import { getRequiredVerificationRoute, getVerificationDestination } from '../utils/verification';
import { confirmPhoneVerification, logout, requestPhoneVerification } from '../store/slices/authSlice';
import { normalizeUserRole } from '../utils/apiResponseUtils';
import BrandLogo from '../components/BrandLogo';
import '../styles/Auth.css';

const PhoneVerification = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { user } = useSelector((state) => state.auth);
  const [phone, setPhone] = useState(() => {
    const profilePhone = String(user?.phone || '').trim();
    const digits = profilePhone.replace(/\D/g, '');
    return /^\+?[0-9 ()-]+$/.test(profilePhone) && digits.length >= 10 && digits.length <= 15 ? profilePhone : '';
  });
  const [otp, setOtp] = useState('');
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const isDriver = normalizeUserRole(user) === 'driver';
  const from = location.state?.from;
  const requiredRoute = getRequiredVerificationRoute(user);
  const destination = getVerificationDestination(user, from);
  if (requiredRoute === ROUTES.emailVerification) {
    return <Navigate to={requiredRoute} replace state={location.state} />;
  }
  if (!requiredRoute) {
    return <Navigate to={destination.to} replace state={destination.state} />;
  }

  const sendOtp = async (event) => {
    event.preventDefault();
    if (busy) return;
    setError('');
    setMessage('');
    setRequest(null);
    setOtp('');
    const enteredPhone = phone.trim();
    const digits = enteredPhone.replace(/\D/g, '');
    if (!/^\+?[0-9 ()-]+$/.test(enteredPhone) || digits.length < 10 || digits.length > 15) {
      setError('Enter a valid mobile number, including country code if required.');
      return;
    }
    setBusy(true);
    try {
      const result = await dispatch(requestPhoneVerification({ phone: enteredPhone })).unwrap();
      setRequest(result);
      setMessage('OTP sent. Enter the code sent to your mobile number.');
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (event) => {
    event.preventDefault();
    if (busy) return;
    if (!request || !/^\d{4,8}$/.test(otp)) { setError('Enter a 4?8 digit OTP.'); return; }
    setBusy(true);
    setError('');
    try {
      await dispatch(confirmPhoneVerification({
        requestToken: request.requestToken,
        phone: request.phone,
        otp: otp.trim(),
      })).unwrap();
    } catch (failure) {
      setError(String(failure));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="auth-page py-5">
      <Container style={{ maxWidth: 520 }}>
        <Card className="p-4 shadow-sm">
          <BrandLogo />
          <h1 className="h3 mt-4">Verify your mobile number</h1>
          <p>{isDriver
            ? 'Verify your mobile number to view, accept, and manage orders.'
            : 'Verify your mobile number to book a delivery.'}</p>
          {error && <Alert variant="danger" role="alert">{error}</Alert>}
          {message && <Alert variant="success" role="status">{message}</Alert>}
          <Form onSubmit={sendOtp}>
            <Form.Group className="mb-3" controlId="verification-phone">
              <Form.Label>Mobile number</Form.Label>
              <Form.Control type="tel" autoComplete="tel" required value={phone} disabled={busy}
                onChange={(event) => {
                  setPhone(event.target.value);
                  setRequest(null);
                  setOtp('');
                  setMessage('');
                  setError('');
                }} />
              <Form.Text>You can edit this number before requesting an OTP.</Form.Text>
            </Form.Group>
            <Button type="submit" disabled={busy || !phone.trim()}>
              {busy && <Spinner size="sm" className="me-2" />}Send OTP
            </Button>
          </Form>
          {request && (
            <Form onSubmit={verifyOtp} className="mt-4">
              <Form.Group className="mb-3" controlId="verification-otp">
                <Form.Label>OTP</Form.Label>
                <Form.Control autoComplete="one-time-code" inputMode="numeric" required
                  value={otp} disabled={busy} onChange={(event) => setOtp(event.target.value.replace(/\D/g, '').slice(0, 8))} />
              </Form.Group>
              <Button type="submit" disabled={busy || !otp.trim()}>Verify mobile number</Button>
            </Form>
          )}
          <Button variant="link" className="mt-3 align-self-start" disabled={busy}
            onClick={() => dispatch(logout())}>Sign out</Button>
        </Card>
      </Container>
    </main>
  );
};

export default PhoneVerification;
